import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { pairwise, startWith } from 'rxjs/operators';
import { ModalService } from '@app/core/services/modal.service';
import { ContextMenuService } from '@shared/directives/context-menu/context-menu.service';
import { HotkeyService } from '@app/core/services/hotkey.service';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { TableCellComponent } from '@shared/components/table/table-cell/table-cell.component';
import { Columns } from '@shared/interfaces/table/column-config';
import {
  ArrowDownAZ,
  ArrowDownUp,
  ArrowUpAZ,
  Clipboard,
  Download,
  LucideAngularModule,
  RefreshCw,
  X
} from 'lucide-angular';
import { sortDataset } from '@app/core/utils/sort-data';
import { TablePaginationComponent } from '@shared/components/table/table-pagination/table-pagination.component';
import { SortColumn } from '@shared/interfaces/table/sort-column';
import { ResizeColumnDirective } from '@shared/directives/resize-column/resize-column.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LoadingService } from '@app/core/services/loading.service';
import {
  TableColumnSelectorComponent
} from '@shared/components/table/table-column-selector/table-column-selector.component';
import { NestedKeyOf } from '@app/core/utils/nestedkey';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { AlertService } from '@app/core/services/alert.service';
import { HeaderService } from '@app/core/services/header.service';
import { ToastService } from '@app/core/services/toast.service';
import { TooltipDirective } from '@shared/directives/tooltip/tooltip.directive';
import { TableConfigService } from '@app/core/services/table-config.service';
import { TableSavedSettings } from '@shared/interfaces/table/table-saved-settings';
import { ContextMenuDirective } from '@shared/directives/context-menu/context-menu.directive';
import { TablePageSize } from '@shared/interfaces/table/table-pagesize';
import { TablePagesizeComponent } from '@shared/components/table/table-pagesize/table-pagesize.component';
import { LoadingOverlayComponent } from '@shared/components/loading-overlay/loading-overlay.component';
import { SortParam } from '@app/core/constants/sort-param';
import { SortDir } from '@app/core/constants/sort-dir';
import { SortDirection } from '@app/api/dmc';
import { HttpErrorResponse } from '@angular/common/http';
import { isJsonParseHttpError } from '@app/core/utils/is-json-parse-error';
import { Observable, Subject, Subscription } from 'rxjs';
import { StatusCellComponent } from '@shared/components/table/cells/status-cell/status-cell.component';
import { DateCellComponent } from '@shared/components/table/cells/date-cell/date-cell.component';
import { CurrencyCellComponent } from '@shared/components/table/cells/currency-cell/currency-cell.component';
import { DecimalCellComponent } from '@shared/components/table/cells/decimal-cell/decimal-cell.component';
import { TranslateCellComponent } from '@shared/components/table/cells/translate-cell/translate-cell.component';
import { DefaultToCellComponent } from '@shared/components/table/cells/default-to-cell/default-to-cell.component';
import { DATE_FORMAT } from '@app/core/constants/date-format';
import { PreferencesService } from '@app/core/services/preferences.service';
import { CURRENCY_FORMAT } from '@app/core/constants/currency-format';
import { MenuAction } from '@shared/interfaces/header/menu-action';

/**
 * Generic, configurable data table component with optional server-side pagination,
 * sorting, column management, and persistence of user settings.
 *
 * @template T The row/item type rendered by the table.
 */
@Component({
  selector: 'app-table',
  imports: [
    TableCellComponent,
    NgStyle,
    NgClass,
    LucideAngularModule,
    TablePaginationComponent,
    ResizeColumnDirective,
    TranslatePipe,
    TableColumnSelectorComponent,
    CdkDrag,
    CdkDropList,
    NgTemplateOutlet,
    TooltipDirective,
    ContextMenuDirective,
    TablePagesizeComponent,
    LoadingOverlayComponent,
  ],
  providers: [DatePipe, CurrencyPipe, DecimalPipe],
  templateUrl: './table.component.html',
  styleUrl: './table.component.css'
})
export class TableComponent<T> implements OnInit, OnChanges, OnDestroy {
  /**
   * Directives that handle column resizing for each visible column.
   * Populated after view init when columns are rendered.
   */
  @ViewChildren(ResizeColumnDirective) resizeDirectives!: QueryList<ResizeColumnDirective>;

  /**
   * References to each rendered row, used for keyboard navigation focus.
   */
  @ViewChildren('rowEl') public rowElements!: QueryList<ElementRef<HTMLTableRowElement>>;

  // ─── Keyboard Navigation (static, shared across all instances) ────────────

  /** Monotonically increasing counter — each instance gets a stable identity. */
  private static nextInstanceId = 0;

  /**
   * Set of instance ids that have `keyboardNavigation: true`.
   * Used to determine which table is the "first" on a page when none is active.
   */
  private static readonly navigableInstances = new Set<number>();

  /** Well-known hotkey ids for the keyboard-nav display entries. */
  private static readonly NAV_HOTKEY_DOWN_ID = 'table-keyboard-nav-down';
  private static readonly NAV_HOTKEY_UP_ID   = 'table-keyboard-nav-up';

  /**
   * The instance id that currently "owns" keyboard navigation, i.e. the table
   * whose row was most recently focused. Null when no table has been touched.
   */
  private static focusedInstanceId: number | null = null;

  /** Returns the lowest registered navigable instance id (first rendered wins). */
  private static lowestNavigableInstanceId(): number | null {
    return [...TableComponent.navigableInstances].reduce<number | null>(
      (min, id) => (min === null || id < min ? id : min), null
    );
  }

  // ─── Instance fields ───────────────────────────────────────────────────────

  private readonly instanceId = TableComponent.nextInstanceId++;

  /**
   * Backing field for the keyboard-focused row index.
   * Always mutate via the setter so the static focusedInstanceId is kept in sync.
   */
  private _focusedRowIndex: number | null = null;

  /**
   * Index of the row currently focused via keyboard navigation, or null when
   * no row is keyboard-focused.
   */
  public get focusedRowIndex(): number | null { return this._focusedRowIndex; }
  private set focusedRowIndex(value: number | null) {
    this._focusedRowIndex = value;
    if (value !== null) {
      TableComponent.focusedInstanceId = this.instanceId;
    } else if (TableComponent.focusedInstanceId === this.instanceId) {
      TableComponent.focusedInstanceId = null;
    }
  }

  /** Aggregates subscriptions for clean teardown. */
  private readonly subscriptions = new Subscription();

  /** Id of the registered Alt+R hotkey binding, if any. */
  private refreshHotkeyId?: string;

  /** True while the one-shot spin animation is playing on the refresh button. */
  public isRefreshSpinning = false;

  /** True during a refresh when existing data is replaced — shows skeleton rows instead of stale data. */
  protected isRefreshing = false;

  protected get skeletonRowCount(): number[] {
    const count = this.data && this.data.length > 0
      ? Math.min(this.data.length, this.pageSize)
      : Math.min(this.pageSize, 10);
    return Array.from({ length: count });
  }

  /**
   * Table configuration describing columns, selection behavior, paging,
   * optional service callback, and persistence options.
   */
  @Input() public config!: TableConfig<T>;

  /**
   * Client-side dataset used when no `serviceCallback` is provided.
   * When `serviceCallback` exists, this array is updated from the service result.
   */
  @Input() public data: any[] = [];

  /** Current page (1-based). */
  @Input() public currentPage: number = 1;

  /** Array of currently selected row items. */
  @Input() public selectedElements: any[] = [];

  /** Current page size (items per page). */
  @Input() public pageSize: TablePageSize = 10;

  /**
   * Columns currently selected/visible in the table (derived from config and persisted settings).
   */
  public selectedColumns: Columns<T>[] = [];

  /**
   * Keys of active/visible columns. May include nested keys.
   */
  public activeColumns: (keyof T | NestedKeyOf<T>)[] = [];

  /**
   * Optional identifier used to save and restore table settings
   * (e.g., column order/widths and page size).
   */
  @Input() public id?: string;

  /** Whether the table should render in a fullscreen-friendly layout. */
  @Input() public fullScreen: boolean = false;


  /** SortDirections **/
  protected readonly SortDirection = SortDirection;
  protected readonly SortDir = SortDir;

  /**
   * Emits whenever the selection array changes.
   * @fires selectedElementsChange
   */
  @Output() public selectedElementsChange: EventEmitter<any[]> = new EventEmitter();

  /**
   * Emits when the current page changes.
   * @fires currentPageChange
   */
  @Output() public currentPageChange: EventEmitter<number> = new EventEmitter();

  /**
   * Emits when the sort state changes.
   * @fires sortingChange
   */
  @Output() public sortingChange: EventEmitter<SortColumn> = new EventEmitter();

  /**
   * Emits when the page size changes.
   * @fires pageSizeChange
   */
  @Output() public pageSizeChange: EventEmitter<number> = new EventEmitter();

  /**
   * Subject that emits when a search completes (success or error)
   * @private
   */
  private readonly searchCompleteSubject: Subject<{
    success: boolean;
    data?: any[];
    error?: any;
    preservedSelections?: number;
  }> = new Subject();

  /**
   * Observable that emits when search operations complete
   * Components can subscribe to this to react when search finishes
   */
  public searchComplete$: Observable<{
    success: boolean;
    data?: any[];
    error?: any;
    preservedSelections?: number;
  }> = this.searchCompleteSubject.asObservable();

  /** Icon references used in the template. */
  protected readonly arrowDownUp = ArrowDownUp;
  protected readonly arrowDownAZ = ArrowDownAZ;
  protected readonly arrowUpAZ = ArrowUpAZ;
  protected readonly x = X;
  protected readonly download = Download;
  protected readonly clipboard = Clipboard;
  protected readonly refreshCw = RefreshCw;

  /** Total number of rows across all pages (server or client). */
  public dataLength: number = 0;

  /**
   * Current sort state when sorting is applied.
   * `column` is the key used for sorting and `direction` is 'asc' or 'desc'.
   */
  public sort?: { column: any, direction?: SortDir };

  /** Controls visibility of the table container. */
  public visible: boolean = true;

  /** Whether the table is currently in export selection mode. */
  public exportMode: boolean = false;

  /** Whether the table is currently in copy-to-clipboard selection mode. */
  public copyMode: boolean = false;

  /** The most recently right-clicked row, used for context-menu export. */
  private rightClickedRow: any = null;

  /**
   * @param loadingService Service that exposes a loading indicator API.
   * @param alertService Service for surfacing user-facing alerts/toasts.
   * @param tableConfigService Service for persisting and retrieving table settings.
   */
  constructor(
    public readonly loadingService: LoadingService,
    public readonly preferencesService: PreferencesService,
    private readonly alertService: AlertService,
    private readonly headerService: HeaderService,
    private readonly tableConfigService: TableConfigService,
    private readonly translateService: TranslateService,
    private readonly toastService: ToastService,
    private readonly datePipe: DatePipe,
    private readonly currencyPipe: CurrencyPipe,
    private readonly decimalPipe: DecimalPipe,
    private readonly modalService: ModalService,
    private readonly contextMenuService: ContextMenuService,
    private readonly hotkeyService: HotkeyService,
    private readonly el: ElementRef<HTMLElement>,
  ) {
    // Refocus the previously focused row when the page action menu or a row
    // context menu closes (open -> closed transition). startWith(false) seeds
    // the pairwise so the first emission always carries a 'previous' value.
    this.subscriptions.add(
      toObservable(this.headerService.actionsOpen)
        .pipe(startWith(false), pairwise())
        .subscribe(([wasOpen, isOpen]) => {
          if (wasOpen && !isOpen) this.refocusActiveRow();
        })
    );

    this.subscriptions.add(
      toObservable(this.contextMenuService.openState)
        .pipe(startWith(false), pairwise())
        .subscribe(([wasOpen, isOpen]) => {
          if (wasOpen && !isOpen) this.refocusActiveRow();
        })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.refreshHotkeyId) {
      this.hotkeyService.unregister(this.refreshHotkeyId);
    }
    TableComponent.navigableInstances.delete(this.instanceId);
    if (TableComponent.focusedInstanceId === this.instanceId) {
      TableComponent.focusedInstanceId = null;
    }
    if (TableComponent.navigableInstances.size === 0) {
      this.hotkeyService.unregister(TableComponent.NAV_HOTKEY_DOWN_ID);
      this.hotkeyService.unregister(TableComponent.NAV_HOTKEY_UP_ID);
    }
  }

  /**
   * Initializes configuration, restores persisted settings (if enabled),
   * computes selected columns, and optionally triggers initial data fetch.
   */
  public ngOnInit(): void {
    if (this.config) {
      /** PageSize **/
      if (this.config.pageSize) {
        this.pageSize = this.config.pageSize;
      }

      if (this.config.id && !this.id) {
        this.id = this.config.id;
      }

      if (this.config.saveChanges) {
        if (this.id) {
          // Fetch active columns from persisted settings
          this.activeColumns = this.tableConfigService.getColumns(this.id) as (keyof T | NestedKeyOf<T>)[] ?? [];

          // Fetch page size from persisted settings
          this.pageSize = this.tableConfigService.getPageSize(this.id) ?? this.pageSize;

          // Fetch sorting from persisted settings (only when saveSorting is explicitly enabled)
          if (this.config.saveSorting) {
            const sort = this.tableConfigService.getSorting(this.id);
            this.sort = sort ? { column: sort.sortField, direction: sort.sortDirection } : undefined;
          }
        }
      }

      /** Column Setup **/
      this.defineSelectedColumns();

      /** Callback Service **/
      if (this.config.serviceCallback) {
        if (this.config.fetchOnLoad) {
          const sortParam = this.sort ? {
            sortField: this.sort.column,
            sortDirection: this.sort.direction!
          } : undefined;

          this.handleFetch(this.currentPage, sortParam);
        } else {
          this.visible = false;
        }
      }

      /** Export Action — append to header actions **/
      if (this.config.exportable && this.preferencesService.tableExport()) {
        this.appendExportAction();
      }

      /** Copy Action — append to header actions **/
      if (this.isCopyable() && this.preferencesService.tableClipboard()) {
        this.appendCopyAction();
      }

      /** Refresh hotkey **/
      if (this.config.refreshable && (this.config.serviceCallback || this.config.refresh)) {
        this.refreshHotkeyId = `table-refresh-${this.config.id ?? this.instanceId}`;
        this.hotkeyService.register({
          id: this.refreshHotkeyId,
          keys: 'alt+r',
          label: 'hotkeys.label.refreshTable',
          group: 'hotkeys.group.page',
          callback: () => this.refresh(),
        });
      }

      /** Keyboard navigation — register this instance so Shift+Arrow can find it **/
      if (this.config.keyboardNavigation !== false && this.config.selectable) {
        const wasEmpty = TableComponent.navigableInstances.size === 0;
        TableComponent.navigableInstances.add(this.instanceId);

        // Register display-only hotkey entries the first time a navigable table
        // appears on the page so they show up in the hotkey overlay.
        if (wasEmpty) {
          this.hotkeyService.register({
            id: TableComponent.NAV_HOTKEY_DOWN_ID,
            keys: 'shift+arrowdown',
            label: 'hotkeys.label.tableNavNext',
            group: 'hotkeys.group.table',
          });
          this.hotkeyService.register({
            id: TableComponent.NAV_HOTKEY_UP_ID,
            keys: 'shift+arrowup',
            label: 'hotkeys.label.tableNavPrev',
            group: 'hotkeys.group.table',
          });
        }
      }
    }
  }

  /**
   * Reacts to @Input changes. When client-side data changes and there
   * is no service callback, pagination is reset to the first page.
   *
   * @param changes The map of changed inputs.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    // Undefined safeguard
    if (changes['data'] && changes['data'].currentValue === undefined) {
      this.data = [];
    }

    if (changes['data'] && !this.config?.serviceCallback) {
      this.resetPagination();
    }

    // Drop stale keyboard focus when the dataset changes
    if (changes['data']) {
      this.focusedRowIndex = null;
    }

    // React to configuration changes (e.g., columns reference updated)
    if (changes['config'] && !changes['config'].firstChange) {
      if (this.config.activeColumns) {
        this.activeColumns = this.config.activeColumns;
      }
      this.defineSelectedColumns();

      // Wait for DOM to reflect new headers and cells, then reinitialize resizers
      setTimeout(() => {
        this.reinitializeResizeDirectives();
      }, 0);
    }
  }

  /**
   * Triggers a search by resetting pagination and delegating to the fetch handler.
   * Returns an Observable that emits when the search completes.
   * Intended to be callable from parent components (e.g., after filters change).
   *
   * Optionally attempts to preserve selected elements if they still exist in the new dataset.
   *
   * @param preserveSelection Whether to attempt to preserve selected elements after search (default: false)
   * @returns Observable that emits when search completes with success/error status
   *
   * @example
   * // In parent component - without preservation (default)
   * this.table.search().subscribe({
   *   next: (result) => {
   *     if (result.success) {
   *       console.log('Search completed, selections cleared');
   *     }
   *   }
   * });
   *
   * @example
   * // With selection preservation
   * this.table.search(true).subscribe({
   *   next: (result) => {
   *     if (result.success) {
   *       console.log('Search completed successfully');
   *       console.log('Preserved selections:', result.preservedSelections);
   *     }
   *   }
   * });
   */
  public search(preserveSelection: boolean = false): Observable<{ success: boolean; data?: any[]; error?: any; preservedSelections?: number }> {
    // Store current selections before search if preservation is enabled
    const previousSelections = preserveSelection ? [...this.selectedElements] : undefined;

    this.resetPagination();
    this.handleFetch(this.currentPage, undefined, this.pageSize, previousSelections);

    return this.searchComplete$;
  }

  /**
   * Tooltip for the refresh button — combines the translated label with the
   * platform-formatted hotkey label (e.g. "Opdater tabel (Alt R)").
   */
  public get refreshTooltip(): string {
    const label = this.translateService.instant('table.refresh');
    return `${label} (${this.hotkeyService.formatLabel('alt+r')})`;
  }

  /**
   * Re-runs the table's data fetch:
   *   - `serviceCallback`-driven tables re-fetch at the current page and sort order
   *   - Tables using `[data]` plus a `config.refresh` callback delegate to that callback
   *
   * Intended for the refresh button — use `search()` when filters change and you
   * need to reset to page 1.
   */
  public refresh(): void {
    // Trigger one-shot spin; clear after the animation duration (600 ms)
    this.isRefreshSpinning = true;
    setTimeout(() => (this.isRefreshSpinning = false), 600);

    if (this.config?.serviceCallback) {
      const sortParam = this.sort
        ? { sortField: this.sort.column, sortDirection: this.sort.direction! }
        : undefined;
      this.handleFetch(this.currentPage, sortParam);
    } else if (this.config?.refresh) {
      this.config.refresh();
    }
  }

  /**
   * Document-level keydown handler powering Shift+↑/↓ row navigation and
   * Space selection toggling.
   *
   * Listening at document level (instead of the host element) means the user
   * can start navigating the table immediately — no need to tab into it first.
   * The Shift modifier makes the intent unambiguous and avoids conflicts with
   * normal page scrolling or text navigation.
   *
   * On pages with multiple navigable tables, only the "active" table responds:
   *   • If the user has already touched a table (clicked a row or used Shift+Arrow),
   *     that table continues to own the keys.
   *   • If no table has been touched yet, the first-rendered table (lowest instanceId) wins.
   *
   * Suppressed when the user is typing in an input, or when any blocking UI
   * (modal, page action menu, row context menu) is open.
   */
  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (this.config?.keyboardNavigation === false || !this.config.selectable) return;

    const target = event.target as HTMLElement | null;
    const tag = target?.tagName?.toLowerCase();
    const inTextField = tag === 'input' || tag === 'textarea' || (target?.isContentEditable ?? false);

    // When Shift+Arrow is pressed while a text field is focused, treat it as an
    // intent to leave the field and start navigating — blur the input so the
    // keystrokes no longer get intercepted by text-selection behaviour.
    // For all other keys we respect the field focus and do nothing.
    const isShiftArrow = event.shiftKey && (event.key === 'ArrowDown' || event.key === 'ArrowUp');
    if (inTextField) {
      if (isShiftArrow) {
        (target as HTMLElement).blur();
      } else {
        return;
      }
    }

    // Ignore while blocking UI is open
    if (this.isNavigationBlocked()) return;

    // Shift+Arrow navigates rows. Shift is required so bare arrow keys still
    // scroll the page and work normally everywhere else on the page.
    if (isShiftArrow) {
      if (!this.isActiveNavigableTable()) return;
      event.preventDefault();
      this.moveFocus(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === ' ' && this.focusedRowIndex !== null && this.isActiveNavigableTable()) {
      // Space toggles selection on the focused row once navigation is active.
      event.preventDefault();
      this.toggleSelectionOnFocusedRow();
    }
  }

  /**
   * Returns true when this table instance should handle the Shift+Arrow key.
   * Ensures only one table responds on multi-table pages.
   */
  private isActiveNavigableTable(): boolean {
    if (TableComponent.focusedInstanceId !== null) {
      return TableComponent.focusedInstanceId === this.instanceId;
    }
    // No table has been touched yet — the first-rendered one (lowest id) wins
    return TableComponent.lowestNavigableInstanceId() === this.instanceId;
  }

  /**
   * Returns true when arrow navigation should yield because some other
   * piece of UI is currently consuming user input.
   */
  private isNavigationBlocked(): boolean {
    const modalOpen = this.modalService.modalState() === 'modal_opened';
    const insideModal = modalOpen && this.el.nativeElement.closest('[cdktrapfocus]') !== null;
    return (
      (modalOpen && !insideModal) ||
      this.headerService.actionsOpen() ||
      this.contextMenuService.open()
    );
  }

  /**
   * Moves the keyboard focus by `delta` rows, clamped to the visible page.
   * In single-select mode the navigated row is also selected automatically.
   */
  private moveFocus(delta: number): void {
    const rows = this.getVisibleRowCount();
    if (rows === 0) return;

    let next: number;
    if (this.focusedRowIndex === null) {
      // First arrow press from a blank state — enter from the top or bottom
      next = delta > 0 ? 0 : rows - 1;
    } else {
      next = Math.max(0, Math.min(rows - 1, this.focusedRowIndex + delta));
    }

    this.focusRow(next);

    if (this.config?.selectableType === 'single') {
      this.selectFocusedRow();
    }
  }

  /**
   * Focuses the row at the given index and updates `focusedRowIndex`.
   * Safe to call after a render — uses ViewChildren to find the actual <tr>.
   */
  private focusRow(index: number): void {
    this.focusedRowIndex = index;
    const el = this.rowElements?.get(index);
    el?.nativeElement.focus();
  }

  /**
   * Selects the currently focused row (used in single-select mode where the
   * navigation cursor IS the selection).
   */
  private selectFocusedRow(): void {
    if (this.focusedRowIndex === null) return;
    const row = this.getVisibleRows()[this.focusedRowIndex];
    if (!row) return;
    this.selectedElements = [row];
    this.emitSelectedRows();
  }

  /**
   * Toggles selection of the focused row, honouring single vs multiple mode.
   */
  private toggleSelectionOnFocusedRow(): void {
    if (this.focusedRowIndex === null) return;
    const row = this.getVisibleRows()[this.focusedRowIndex];
    if (!row) return;

    const idx = this.selectedElements.indexOf(row);
    if (this.config?.selectableType === 'single') {
      // Mirror selectRow single-select behaviour: replace the selection
      this.selectedElements = idx === -1 ? [row] : [];
    } else if (idx === -1) {
      this.selectedElements.push(row);
    } else {
      this.selectedElements.splice(idx, 1);
    }

    this.emitSelectedRows();
  }

  /**
   * Tracks which row is the keyboard navigation cursor when the user
   * clicks or tabs into a row. Called from the template.
   */
  public onRowKeyboardFocus(index: number): void {
    if (this.config?.keyboardNavigation === false) return;
    this.focusedRowIndex = index;
  }

  /**
   * Restores keyboard focus to the previously focused row, used after a
   * blocking menu/modal closes. No-op when no row had focus.
   */
  private refocusActiveRow(): void {
    if (this.focusedRowIndex === null) return;
    // Defer one tick so the menu DOM is fully gone before we steal focus.
    // Re-check blocking state inside the callback — if the menu close triggered
    // a modal to open, we must not steal focus back or the modal's focus trap breaks.
    setTimeout(() => {
      if (this.isNavigationBlocked()) return;
      const el = this.rowElements?.get(this.focusedRowIndex!);
      el?.nativeElement.focus();
    });
  }

  /**
   * Returns the rows currently visible on screen — server-paginated tables
   * already render the full slice, client-paginated tables need slicing here
   * to mirror what the template shows.
   */
  private getVisibleRows(): any[] {
    if (!this.data) return [];
    if (this.config?.serviceCallback) return this.data;
    return this.data.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);
  }

  private getVisibleRowCount(): number {
    return this.getVisibleRows().length;
  }

  /**
   * Handles fetching data from the configured `serviceCallback` if present.
   * Applies loading state, resets selection, and updates `data`/`dataLength`
   * from the observable result. Emits completion status via searchCompleteSubject.
   *
   * Attempts to preserve selected elements from previousSelections if they exist in new data.
   *
   * @param page The page index to fetch (1-based).
   * @param sort Optional sort parameter passed to the service.
   * @param pageSize Optional page size (defaults to current `this.pageSize`).
   * @param previousSelections Optional array of previously selected elements to attempt to preserve.
   * @private
   */
  private handleFetch(
    page: number,
    sort?: SortParam,
    pageSize: TablePageSize = this.pageSize,
    previousSelections?: any[]
  ): void {
    if (this.config.serviceCallback && typeof this.config.serviceCallback === 'function') {

      /** Set Loading **/
      this.loadingService.start();

      /** Show skeleton rows when refreshing existing data **/
      this.isRefreshing = this.data !== undefined && this.data.length > 0;

      /** Show Table **/
      this.visible = true;

      /** Close all errors **/
      this.alertService.clearAll();

      /** Fetch Data from Service **/
      this.config.serviceCallback(pageSize, page, sort).subscribe({
          next: (result: any) => {
            if (this.config?.oData) {
              // Populate data from OData response
              this.data = result.value;
              this.dataLength = result['@odata.count'];
            } else {
              // Populate data from generic API response
              this.data = result.results ?? [];
              this.dataLength = result.totalHitCount ?? this.data?.length;
            }

            // Reset keyboard focus when fresh data arrives
            this.focusedRowIndex = null;

            // Attempt to preserve selected elements (or clear if not preserving)
            const preservedCount = this.preserveSelectedElements(previousSelections);

            // Emit success
            this.searchCompleteSubject.next({
              success: true,
              data: this.data,
              preservedSelections: preservedCount
            });
          },
          error: (error: HttpErrorResponse) => {
            this.loadingService.stop();
            this.isRefreshing = false;

            if (isJsonParseHttpError(error)) {
              this.alertService.danger('header', 'error.server.internal', true);
              console.error('Error parsing JSON response from service:', error.error);
            }

            this.clearData();

            // Clear selections on error
            this.unselectAll();

            // Emit error
            this.searchCompleteSubject.next({
              success: false,
              error: error,
              preservedSelections: 0
            });
          },
          complete: () => {
            this.loadingService.stop();
            this.isRefreshing = false;
          }
        }
      )
    }
  }

  /**
   * Attempts to preserve selected elements after a data fetch.
   * Only elements that still exist in the new dataset will remain selected.
   * If previousSelections is undefined or empty, all selections are cleared.
   *
   * @param previousSelections Array of previously selected elements (undefined = clear all)
   * @returns Number of selections that were successfully preserved
   * @private
   */
  private preserveSelectedElements(previousSelections?: any[]): number {
    if (!previousSelections || previousSelections.length === 0) {
      this.unselectAll();
      return 0;
    }

    if (!this.data || this.data.length === 0) {
      this.unselectAll();
      return 0;
    }

    const newSelections: any[] = [];

    for (const previousElement of previousSelections) {
      // Find matching element in new data
      const matchingElement = this.findMatchingElement(previousElement, this.data);

      if (matchingElement) {
        newSelections.push(matchingElement);
      }
    }

    this.selectedElements = newSelections;

    if (newSelections.length > 0) {
      this.emitSelectedRows();
    }

    return newSelections.length;
  }

  /**
   * Finds a matching element in the dataset by comparing JSON stringified values.
   * This works for most cases but can be overridden for custom matching logic.
   *
   * @param element The element to find
   * @param dataset The dataset to search in
   * @returns The matching element or undefined if not found
   * @private
   */
  private findMatchingElement(element: any, dataset: any[]): any {
    // Try to find by comparing JSON strings
    const elementString = JSON.stringify(element);

    return dataset.find(item => {
      const itemString = JSON.stringify(item);
      return itemString === elementString;
    });
  }

  /**
   * Gets a human-readable identifier for an element (for logging purposes).
   * Attempts to use common ID fields, falls back to stringified object.
   *
   * @param element The element to identify
   * @returns A string identifier
   * @private
   */
  private getElementIdentifier(element: any): string {
    if (!element) return 'undefined';

    // Try common ID fields
    if (element.id) return `id: ${element.id}`;
    if (element.identification) return `identification: ${element.identification}`;
    if (element.meteringPointIdentification) return `mp: ${element.meteringPointIdentification}`;
    if (element.name) return `name: ${element.name}`;

    // Fallback to truncated JSON
    const json = JSON.stringify(element);
    return json.length > 50 ? json.substring(0, 50) + '...' : json;
  }

  /**
   * Handles page change events by fetching the new page when using a service.
   */
  public handlePageChange(): void {
    this.handleFetch(this.currentPage);
  }

  /**
   * Define Selected Columns.
   * If active columns have been set (via config or persisted settings), use those.
   * Otherwise use all configured columns.
   */
  public defineSelectedColumns(): void {
    if (this.config.activeColumns && this.activeColumns.length === 0) {
      this.activeColumns = this.config.activeColumns;
    }

    if (this.activeColumns && this.activeColumns.length > 0) {
      this.selectedColumns =  this.fetchAvailableColumns(this.activeColumns);
    } else {
      this.selectedColumns = this.fetchColumnConfigs(this.config.columns);
    }
  }

  /**
   * Builds a full column config list from an array of keys by looking up
   * their definitions in `config.columns`.
   *
   * @param array The ordered list of column keys to include.
   * @returns The resolved column configuration list.
   * @private
   */
  private fetchAvailableColumns(array: (keyof T | NestedKeyOf<T>)[]): Columns<T>[] {
    return this.fetchColumnConfigs(array.map((key) => ({ key } as Columns<T>)))
  }

  /**
   * Resolves a list of `Columns<T>` against the component config and
   * restores any persisted per-column settings (e.g., width).
   *
   * @param columns A list of columns or column placeholders (with keys set).
   * @returns The resolved column configuration list.
   */
  private fetchColumnConfigs(columns: Columns<T>[]): Columns<T>[] {
    const returnArray: Columns<T>[] = [];

    const storedSettings: TableSavedSettings | undefined = this.id ? this.tableConfigService.fetchSettings(this.id) : undefined;

    for (const element of columns) {
      const column = this.config.columns.find(x => x.key === element.key);
      if (column) {
        if (storedSettings?.config?.[String(element.key)]) {
          /** Width **/
          if (storedSettings.config[String(element.key)].width) {
            column.width = storedSettings.config[String(element.key)].width;
          }
        }
        returnArray.push(column);
      }
    }

    return returnArray;
  }

  /**
   * Persists the current column order to storage when an `id` is available.
   *
   * @param columns The ordered list of column keys.
   * @private
   */
  private saveSettings(columns: (keyof T | NestedKeyOf<T>)[]): void {
    if (this.id) {
      this.tableConfigService.reorderColumns(this.id, columns as string[]);

      if (this.config.title) {
        this.tableConfigService.updateTitle(this.id, this.config.title);
      }
    }
  }

  /**
   * Resets pagination to the first page (1-based).
   */
  public resetPagination(): void {
    this.currentPage = 1;
  }

  /**
   * Selects or clears selection of all visible rows depending on current state.
   * Emits the updated selection array.
   */
  public selectAll(): void {
    if (this.data && (this.config?.selectable || this.exportMode || this.copyMode)) {
      if (this.selectedElements.length === this.data.length) {
        this.selectedElements = [];
      } else {
        this.selectedElements = [ ... this.data ?? '' ];
      }

      this.emitSelectedRows();
    }
  }

  /**
   * Clears the selection if any rows are selected and emits the change.
   */
  public unselectAll(): void {
    if (this.data && this.config?.selectable && this.selectedElements.length > 0) {
      this.selectedElements = [];
      this.emitSelectedRows();
    }
  }

  /**
   * Handles click selection on a row, supporting single- and multi-select.
   * Right-click selects only the clicked row and emits immediately (for context menus).
   *
   * @param event The mouse event from the row click.
   * @param selectedRow The row item that was clicked.
   */
  public selectRow(event: MouseEvent, selectedRow: any) : void {
    /** Right Click Handling — always track the row for context-menu export **/
    if (event.button === 2) {
      this.rightClickedRow = selectedRow;
      if (this.config.selectable || this.exportMode || this.copyMode) {
        this.selectedElementsChange.emit([selectedRow]);
      }
      return;
    }

    if (!this.config.selectable && !this.exportMode && !this.copyMode) return;

    const indexOf: number = this.selectedElements.indexOf(selectedRow);
    /** If the row is already selected, remove it — skip single constraint in export/copy mode **/
    if (this.config?.selectableType === 'single' && !this.exportMode && !this.copyMode && indexOf === -1) {
      this.selectedElements = [];
    }
    /** Toggle Row **/
    if (indexOf === -1) {
      this.selectedElements.push(selectedRow);
    } else {
      this.selectedElements.splice(indexOf, 1);
    }

    this.emitSelectedRows();
  }

  /**
   * Checks whether a given row is currently selected.
   *
   * @param selectedRow The row item to test.
   * @returns True if the row is selected; otherwise false.
   */
  public rowIsSelected(selectedRow: any): boolean {
    return this.selectedElements.indexOf(selectedRow) >= 0;
  }

  public sortColumn(column: Columns<any>): void {
    if (!this.isColumnSortable(column)) {
      return;
    }

    this.updateSortState(column);

    if (this.config.serviceCallback) {
      this.sortWithService(column);
    } else {
      this.sortLocally(column);
    }
  }

  /**
   * Check if column can be sorted
   * A column is sortable if it has a key and sortable is not explicitly false
   */
  private isColumnSortable(column: Columns<any>): column is Columns<any> & { key: string } {
    return !!(column.key && column.sortable !== false);
  }

  /**
   * Update the sort state (toggle direction or set new column)
   */
  private updateSortState(column: Columns<any> & { key: string }): void {
    if (this.sort?.column === column.key) {
      // Toggle direction
      this.sort.direction = this.sort.direction === SortDir.asc ? SortDir.desc : SortDir.asc;
    } else {
      // New column - default to descending
      this.sort = { column: column.key, direction: SortDir.desc };
    }
  }

  /**
   * Handle server-side sorting (with service callback)
   */
  private sortWithService(column: Columns<any> & { key: string }): void {
    this.currentPage = 1;

    const sortParam: SortParam = {
      sortField: column.key,
      sortDirection: this.sort!.direction!
    };

    // Save if configured (only when saveSorting is explicitly enabled)
    if (this.config?.saveChanges && this.config.saveSorting && this.id) {
      this.tableConfigService.updateSorting(this.id, sortParam);
      if (this.config.title) {
        this.tableConfigService.updateTitle(this.id, this.config.title);
      }
    }

    this.handleFetch(this.currentPage, sortParam);
  }

  /**
   * Handle client-side sorting (in-memory data)
   */
  private sortLocally(column: Columns<any> & { key: string }): void {
    if (this.data && this.sort) {
      sortDataset(this.data, column.key, this.sort.direction);
    }
  }

  /**
   * Emits an explicit sorting change event (useful for external integrations).
   *
   * @param column The column key being sorted.
   * @param direction The sort direction ('asc' | 'desc').
   */
  public emitSortingChange(column: string, direction: SortDir): void {
    this.sortingChange.emit({ column, direction });
  }

  /**
   * Emits the current `selectedElements` array to consumers.
   */
  public emitSelectedRows(): void {
    this.selectedElementsChange.emit(this.selectedElements);
  }

  /**
   * Handles drag-and-drop reordering of visible columns.
   * Persists order (if enabled) and reinitializes resize directives after DOM update.
   *
   * @param event CDK drag-drop event containing previous and current indices.
   */
  public dropColumn(event: CdkDragDrop<any>): void {
    moveItemInArray(this.selectedColumns, event.previousIndex, event.currentIndex);

    if (this.config.saveChanges) {
      const reducedColumns = this.selectedColumns.map((x: Columns<T>) => { return x.key });
      this.saveSettings(reducedColumns);
    }

    // Wait for the DOM to update after the drop
    setTimeout(() => {
      this.reinitializeResizeDirectives();
    }, 0);
  }

  /**
   * Handles column visibility/order changes triggered by the column selector.
   * Persists new order when configured to save changes.
   */
  public onColumnChange(): void {
    if (this.config.saveChanges) {
      const reducedColumns = this.selectedColumns.map((x: Columns<T>) => { return x.key });
      this.saveSettings(reducedColumns);
    }
  }

  /**
   * Clears all persisted settings (column order/widths, etc.) for this table and
   * recomputes the selected columns based on the base configuration.
   */
  public resetSettings(): void {
    if (this.id) {
      this.tableConfigService.removeColumnSettings(this.id);

      this.activeColumns = [];

      this.defineSelectedColumns();
    }
  }

  /**
   * Reinitialize Resize Directives.
   * Ensures column widths are recalculated after DnD or table resize.
   *
   * @private
   */
  private reinitializeResizeDirectives() {
    for (const directive of this.resizeDirectives) {
      directive.reinitialize();
    }
  }

  private clearData(): void {
    this.data = [];
    this.dataLength = 0;
  }

  /**
   * Handles page size changes:
   * - Resets to first page
   * - Optionally fetches from service
   * - Persists the new page size if saving is enabled
   *
   * @param pageSize The newly selected page size.
   */
  public handlePageSizeChange(pageSize: TablePageSize): void {
    // Ignore intermediate string emissions that can arrive before SelectDirective
    // finishes converting the raw DOM value to a number.
    if (typeof pageSize !== 'number') return;

    this.currentPage = 1;

    if (this.config?.serviceCallback) {
      this.handleFetch(this.currentPage);
    }

    if (this.config?.id && this.config?.saveChanges) {
      this.tableConfigService.updatePageSize(this.config.id, this.pageSize);
    }
  }

  /**
   * TrackBy function to improve ngFor performance by returning a stable identity.
   * Combines the item's identity with its index so that duplicate/equal rows in the
   * source data (e.g. literal API duplicates) can never collide and trip NG0955.
   *
   * @param index Index of the item in the collection.
   * @param item The row item.
   * @returns A unique identity for the row.
   */
  public trackByFn(index: number, item: any): any {
    const identity = item?.id ?? JSON.stringify(item);
    return `${identity}-${index}`;
  }

  /**
   * Exports the table data to a CSV file and triggers a browser download.
   *
   * For tables configured as single-select, calling this method enters an
   * "export mode" where the user can multi-select rows. An alert banner is
   * shown above the table with an export link. Calling this method again
   * (or clicking the export link) performs the actual export and exits
   * export mode.
   *
   * For multi-select or non-selectable tables, the export happens immediately.
   *
   * - If rows are selected, only those rows are exported.
   * - If no rows are selected, all rows in the current dataset are exported.
   * - Uses the currently visible columns (selectedColumns) for headers and values.
   * - Column headers are translated using the TranslateService.
   * - Filename is derived from config.title (translated) + current date,
   *   or "export-{date}" if no title is configured.
   *
   * @example
   * // From a parent component
   * this.table.exportCsv();
   */
  /**
   * Appends an export action to the existing header menu actions.
   * The action is added as the last element with a visual separator (breaker).
   * If an export action already exists in the header, it is not added again.
   * @private
   */
  private appendExportAction(): void {
    const currentActions = this.headerService.menu();

    // Don't add if an export action is already present
    if (currentActions.some(a => a.id === 'export')) {
      return;
    }

    const exportAction: MenuAction = {
      id: 'export',
      title: 'common.export',
      icon: Download,
      callback: () => this.exportCsv(),
      breaker: false,
      hotkey: 'mod+shift+e',
      disabled: () => !this.visible,
    };

    // Place the separator on the last existing action so export+copy are visually grouped
    if (currentActions.length > 0) {
      const updated = [...currentActions];
      updated[updated.length - 1] = { ...updated[updated.length - 1], breaker: true };
      this.headerService.setActions([...updated, exportAction]);
    } else {
      this.headerService.setActions([...currentActions, exportAction]);
    }
  }

  /**
   * Appends a copy-to-clipboard action to the existing header menu actions.
   * The action is added as the last element with a visual separator (breaker).
   * If a copy action already exists in the header, it is not added again.
   * @private
   */
  private appendCopyAction(): void {
    const currentActions = this.headerService.menu();

    // Don't add if a copy action is already present
    if (currentActions.some(a => a.id === 'copy-to-clipboard')) {
      return;
    }

    const copyAction: MenuAction = {
      id: 'copy-to-clipboard',
      title: 'common.copy_to_clipboard',
      icon: Clipboard,
      callback: () => this.copyCsvToClipboard(),
      breaker: false,
      hotkey: 'mod+shift+c',
      disabled: () => !this.visible,
    };

    // Only place a separator if export was not already added (export handles its own separator placement)
    const exportAlreadyAdded = currentActions.some(a => a.id === 'export');
    if (!exportAlreadyAdded && currentActions.length > 0) {
      const updated = [...currentActions];
      updated[updated.length - 1] = { ...updated[updated.length - 1], breaker: true };
      this.headerService.setActions([...updated, copyAction]);
    } else {
      this.headerService.setActions([...currentActions, copyAction]);
    }
  }

  /**
   * Returns the context actions for the table rows, automatically appending an
   * export action when `config.exportable` is true, and/or a copy action when
   * `config.copyable` is enabled. Clipboard export inherits the export setting
   * unless `copyable` explicitly opts out. Each acts on only the right-clicked row.
   */
  public get computedContextActions(): MenuAction[] {
    const actions = this.config?.contextActions ?? [];
    const result: MenuAction[] = [...actions];

    if (this.config?.exportable && this.preferencesService.tableExport()) {
      result.push({
        id: 'export-row',
        title: 'common.export',
        icon: this.download,
        breaker: actions.length > 0 && result.length === actions.length,
        callback: () => this.performExport(this.rightClickedRow ? [this.rightClickedRow] : undefined),
      });
    }

    if (this.isCopyable() && this.preferencesService.tableClipboard()) {
      result.push({
        id: 'copy-row',
        title: 'common.copy_to_clipboard',
        icon: this.clipboard,
        breaker: actions.length > 0 && result.length === actions.length,
        callback: () => this.performCopy(this.rightClickedRow ? [this.rightClickedRow] : undefined),
      });
    }

    return result;
  }

  /**
   * Determines whether clipboard export is enabled for this table.
   * Clipboard export follows `exportable` by default, while an explicit
   * `copyable` value overrides that default.
   */
  private isCopyable(): boolean {
    return this.config?.copyable ?? this.config?.exportable ?? false;
  }

  public exportCsv(): void {
    // If single-select, no rows selected, and not yet in export mode — enter multi-select export mode
    if (this.config?.selectableType === 'single' && !this.exportMode && this.selectedElements.length === 0) {
      this.exportMode = true;
      this.emitSelectedRows();
      return;
    }

    this.performExport();
  }

  /**
   * Cancels export mode without exporting.
   * Clears selections and hides the export alert banner.
   */
  public cancelExport(): void {
    this.exportMode = false;
    this.selectedElements = [];
    this.emitSelectedRows();
  }

  /**
   * Copies table data as CSV to the clipboard.
   *
   * Mirrors the behaviour of `exportCsv()`: for single-select tables with no
   * rows currently selected this enters copy mode so the user can pick rows
   * before confirming. For multi-select or non-selectable tables the copy
   * happens immediately.
   */
  public copyCsvToClipboard(): void {
    if (this.config?.selectableType === 'single' && !this.copyMode && this.selectedElements.length === 0) {
      this.copyMode = true;
      this.emitSelectedRows();
      return;
    }

    this.performCopy();
  }

  /**
   * Cancels copy mode without copying.
   * Clears selections and hides the copy banner.
   */
  public cancelCopy(): void {
    this.copyMode = false;
    this.selectedElements = [];
    this.emitSelectedRows();
  }

  /**
   * Builds the CSV string from the current (or overridden) rows and writes it
   * to the clipboard via the Clipboard API, then shows a success toast.
   *
   * @param rowsOverride When provided, copies exactly these rows and skips
   *   copy-mode cleanup.
   * @private
   */
  private performCopy(rowsOverride?: any[]): void {
    const rows = rowsOverride ?? (this.selectedElements.length > 0 ? this.selectedElements : this.data);

    if (!rows || rows.length === 0) {
      this.copyMode = false;
      return;
    }

    const columns = this.selectedColumns;
    const headers = columns.map(col => this.translateService.instant(col.title));

    const csvRows: string[] = [headers.map(h => this.escapeCsvValue(h)).join(';')];

    for (const row of rows) {
      const values = columns.map(col => {
        const value = this.resolveCellValue(row, col);
        return this.escapeCsvValue(value);
      });
      csvRows.push(values.join(';'));
    }

    const csvContent = csvRows.join('\n');

    navigator.clipboard.writeText(csvContent).then(() => {
      this.toastService.show('common.copy_to_clipboard_success');
    });

    // Exit copy mode (skip when copying a specific row via context menu)
    if (this.copyMode && !rowsOverride) {
      this.copyMode = false;
      this.selectedElements = [];
      this.emitSelectedRows();
    }
  }

  /**
   * Performs the actual CSV export and exits export mode.
   * @param rowsOverride When provided, exports exactly these rows and skips export-mode cleanup.
   * @private
   */
  private performExport(rowsOverride?: any[]): void {
    const rows = rowsOverride ?? (this.selectedElements.length > 0 ? this.selectedElements : this.data);

    if (!rows || rows.length === 0) {
      this.exportMode = false;
      return;
    }

    const columns = this.selectedColumns;
    const headers = columns.map(col => this.translateService.instant(col.title));

    const csvRows: string[] = [headers.map(h => this.escapeCsvValue(h)).join(';')];

    for (const row of rows) {
      const values = columns.map(col => {
        const value = this.resolveCellValue(row, col);
        return this.escapeCsvValue(value);
      });
      csvRows.push(values.join(';'));
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });

    const date = new Date().toISOString().slice(0, 10);
    let filename: string;

    if (this.config.title) {
      const translatedTitle = this.translateService.instant(this.config.title);
      const safeTitle = translatedTitle.replaceAll(/[^a-zA-Z0-9æøåÆØÅ _-]/g, '').replaceAll(/\s+/g, '_');
      filename = `${safeTitle}_${date}.csv`;
    } else {
      filename = `export_${date}.csv`;
    }

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);

    // Exit export mode (skip when exporting a specific row via context menu)
    if (this.exportMode && !rowsOverride) {
      this.exportMode = false;
      this.selectedElements = [];
      this.emitSelectedRows();
    }
  }

  /**
   * Resolves a raw nested value from a dot-separated key path.
   *
   * @param obj The data object to traverse.
   * @param key The dot-separated key path.
   * @returns The resolved value, or undefined if not found.
   * @private
   */
  private resolveNestedValue(obj: any, key: string): any {
    return key.split('.').reduce((acc, part) => acc?.[part], obj);
  }

  /**
   * Resolves the display value for a CSV cell, applying the same transformation
   * logic as the column's cellRenderer would in the UI.
   *
   * - StatusCellComponent: maps to the configured label (translated), strips the color bubble
   * - DateCellComponent: formats the date using DatePipe with the configured or default format
   * - CurrencyCellComponent: formats using CurrencyPipe with the configured or default currency
   * - DecimalCellComponent: formats using DecimalPipe with the configured decimal places
   * - TranslateCellComponent: maps via cellRendererParams then translates
   * - DefaultToCellComponent: falls back to cellRendererParams.defaultTo if the value is empty
   * - All other renderers (Link, Click, etc.): returns the raw value
   *
   * @param row The full row data object.
   * @param col The column configuration, including any cellRenderer and cellRendererParams.
   * @returns The formatted string value for CSV output.
   * @private
   */
  private resolveCellValue(row: any, col: Columns<any>): any {
    const raw = this.resolveNestedValue(row, String(col.key));
    const params = col.cellRendererParams ?? {};

    if (!col.cellRenderer) {
      return raw;
    }

    if (col.cellRenderer === StatusCellComponent) {
      if (raw === null || raw === undefined) return '';
      let normalizedValue: string;
      if (typeof raw === 'boolean') {
        normalizedValue = raw ? '1' : '0';
      } else {
        normalizedValue = String(raw).toLowerCase();
      }
      const statusConfig = params[normalizedValue];
      const label = statusConfig?.label ?? String(raw);
      return label ? this.translateService.instant(label) : '';
    }

    if (col.cellRenderer === DateCellComponent) {
      if (!raw) return '';
      const date = raw instanceof Date ? raw : new Date(raw);
      if (Number.isNaN(date.getTime()) || date.getFullYear() > 3000) return '';
      return this.datePipe.transform(date, params['format'] ?? DATE_FORMAT) ?? '';
    }

    if (col.cellRenderer === CurrencyCellComponent) {
      if (raw === null || raw === undefined) return '';
      return this.currencyPipe.transform(Number(raw), params['format'] ?? CURRENCY_FORMAT) ?? '';
    }

    if (col.cellRenderer === DecimalCellComponent) {
      if (raw === null || raw === undefined) return '';
      const decimals = params['decimals'] ?? 2;
      return this.decimalPipe.transform(raw, `1.${decimals}-${decimals}`, 'da') ?? '';
    }

    if (col.cellRenderer === TranslateCellComponent) {
      if (raw === null || raw === undefined) return '';
      const normalizedParams = Object.fromEntries(
        Object.entries(params).map(([k, v]) => [k.toLowerCase(), v])
      );
      const parts = String(raw).toLowerCase().split(',').map(p => p.trim()).filter(Boolean);
      const translated = parts
        .map(part => normalizedParams[part])
        .filter(Boolean)
        .map(key => this.translateService.instant(String(key)));
      return translated.join(', ');
    }

    if (col.cellRenderer === DefaultToCellComponent) {
      return raw ?? params['defaultTo'] ?? '';
    }

    return raw;
  }

  /**
   * Escapes a value for safe inclusion in a CSV cell.
   * Wraps in double quotes if the value contains the delimiter, quotes, or newlines.
   *
   * @param value The raw cell value.
   * @returns The escaped CSV string.
   * @private
   */
  private escapeCsvValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    const str = String(value);

    if (str.includes(';') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replaceAll('"', '""')}"`;
    }

    return str;
  }
}
